import type { MessageIconsOptions } from '@/app/chat/_common/message-icons'
import {
  MessageCode,
  MessageCSVTable,
  MessageData,
  MessageImage,
  MessageMarkdown,
} from '@/app/chat/_common/message-types'
import type { Message } from '@/hooks/api/_types'

type MessageOutputProps = Readonly<{
  content: Message['messageContent']['content']
  title?: string | null
  enableTypingAnimation?: boolean
  renderIcons?: (options?: MessageIconsOptions) => React.ReactNode
}>

function MessageOutput({
  content,
  enableTypingAnimation,
  title,
  renderIcons = () => null,
}: MessageOutputProps) {
  if (typeof content === 'string') {
    return (
      <MessageMarkdown
        text={content}
        enableTypingAnimation={enableTypingAnimation}
        renderIcons={({ text }) =>
          renderIcons({
            copyOptions: { text },
            // downloadOptions: { content: text, textType: 'plain' },
            // emailOptions: { title },
          })
        }
      />
    )
  }
  if (Array.isArray(content)) {
    return content.map((part, index) => {
      const isLast = index === content.length - 1
      const makeKey = (str: string) => `${str.substring(0, 25)}-${index}`
      const defaultIconOptions: MessageIconsOptions = {
        feedbackOptions: { hidden: !isLast },
        // emailOptions: { title, hidden: !isLast },
      }
      if (typeof part === 'string') {
        return (
          <MessageMarkdown
            key={makeKey(part)}
            text={part}
            enableTypingAnimation={isLast && enableTypingAnimation}
            renderIcons={({ text }) =>
              renderIcons({
                copyOptions: { text },
                // downloadOptions: { content: text, textType: 'plain' },
                ...defaultIconOptions,
              })
            }
          />
        )
      }
      if (part.type === 'text') {
        return (
          <MessageMarkdown
            key={makeKey(part.text)}
            text={part.text}
            enableTypingAnimation={isLast && enableTypingAnimation}
            renderIcons={({ text }) =>
              renderIcons({
                copyOptions: { text },
                // downloadOptions: { content: text, textType: 'plain' },
                ...defaultIconOptions,
              })
            }
          />
        )
      }
      if (part.type === 'data') {
        return (
          <MessageData
            key={makeKey(JSON.stringify(part.data))}
            data={part.data}
            renderIcons={({ dataString }) =>
              renderIcons({
                copyOptions: { text: dataString },
                // downloadOptions: { content: dataString, textType: 'plain' },
                ...defaultIconOptions,
              })
            }
          />
        )
      }
      if (part.type === 'annotated_dataframe') {
        return (
          <MessageCSVTable
            key={makeKey(part.annotatedDataframe.filePath)}
            part={part}
            renderIcons={({ csvString }) =>
              renderIcons({
                copyOptions: { text: csvString },
                //  downloadOptions: { content: csvString, textType: 'csv' },
                ...defaultIconOptions,
              })
            }
          />
        )
      }
      if (part.type === 'annotated_image') {
        return (
          <MessageImage
            key={makeKey(part.annotatedImage.filePath)}
            imgSrc={part.annotatedImage.filePath}
            renderIcons={({ imgSrc }) =>
              renderIcons({
                // downloadOptions: { imgSrc },
                copyOptions: { imgSrc },
                ...defaultIconOptions,
              })
            }
          />
        )
      }

      if (part.type === 'code') {
        return (
          <MessageCode
            key={makeKey(part.code)}
            codeString={part.code}
            language={part.language}
            renderIcons={({ codeString }) =>
              renderIcons({
                // downloadOptions: { content: codeString, textType: 'plain' },
                copyOptions: { text: codeString },
                ...defaultIconOptions,
              })
            }
          />
        )
      }
      return (
        <MessageData
          key={makeKey(JSON.stringify(part))}
          data={part}
          renderIcons={({ dataString }) =>
            renderIcons({
              copyOptions: { text: dataString },
              // downloadOptions: { content: dataString, textType: 'plain' },
              ...defaultIconOptions,
            })
          }
        />
      )
    })
  }
  return (
    <MessageData
      data={content}
      renderIcons={({ dataString }) =>
        renderIcons({
          copyOptions: { text: dataString },
          // downloadOptions: { content: dataString, textType: 'plain' },
          // emailOptions: { title },
        })
      }
    />
  )
}

export { MessageOutput, type MessageOutputProps }
